import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useMemo } from 'react';

/**
 * Custom hook to get form and submission parameters from URL
 */
export const useFormsRouteParams = () => {
	const params = useParams();
	const location = useLocation();
	const history = useHistory();

	const routeInfo = useMemo(() => {
		// Validate and parse route parameters with error handling
		const formId = params.formId ? (
			!isNaN(parseInt(params.formId)) && parseInt(params.formId) > 0 
				? parseInt(params.formId) 
				: null
		) : null;
		
		const submissionId = params.submissionId ? (
			!isNaN(parseInt(params.submissionId)) && parseInt(params.submissionId) > 0 
				? parseInt(params.submissionId) 
				: null
		) : null;
		
		const categoryName = params.categoryName && typeof params.categoryName === 'string' 
			? decodeURIComponent(params.categoryName.trim())
			: null;

		// Determine current view based on path with validation
		let currentView = 'dashboard';
		let hasError = false;
		
		if (location.pathname.includes('/forms/submissions/') && location.pathname.includes('/edit')) {
			if (submissionId) {
				currentView = 'edit-submission';
			} else {
				hasError = true; // Invalid submission ID for edit route
			}
		} else if (location.pathname.includes('/forms/submissions/') && submissionId) {
			currentView = 'view-submission';
		} else if (location.pathname === '/forms/submissions') {
			currentView = 'submissions-list';
		} else if (location.pathname.includes('/forms/category/')) {
			if (categoryName) {
				currentView = 'category-filter';
			} else {
				hasError = true; // Invalid category name
			}
		} else if (location.pathname.includes('/forms/') && formId) {
			currentView = 'form-view';
		} else if (location.pathname.includes('/forms/') && params.formId) {
			// Invalid form ID format
			hasError = true;
		} else if (location.pathname.includes('/forms/submissions/') && params.submissionId && !submissionId) {
			// Invalid submission ID format (but draft IDs are already handled above)
			hasError = true;
		}

		return {
			formId,
			submissionId,
			categoryName,
			currentView,
			isDirectAccess: !!(formId || submissionId || categoryName),
			hasRouteError: hasError,
			routeErrorType: hasError ? (
				params.formId && !formId ? 'invalid-form-id' :
				params.submissionId && !submissionId ? 'invalid-submission-id' :
				params.categoryName && !categoryName ? 'invalid-category' :
				'unknown'
			) : null,
		};
	}, [params, location.pathname]);

	// Navigation helpers
	const navigate = {
		toDashboard: () => history.push('/forms'),
		toForm: (formId) => history.push(`/forms/${formId}`),
		toCategory: (categoryName) => history.push(`/forms/category/${categoryName}`),
		toSubmissions: () => history.push('/forms/submissions'),
		toSubmission: (submissionId) => history.push(`/forms/submissions/${submissionId}`),
		toEditSubmission: (submissionId) => history.push(`/forms/submissions/${submissionId}/edit`),
		back: () => history.goBack(),
		replace: (path) => history.replace(path),
	};

	return {
		...routeInfo,
		navigate,
		location,
		history,
	};
};

/**
 * Custom hook for form-specific route handling
 */
export const useFormRoute = () => {
	const { formId, navigate } = useFormsRouteParams();
	return {
		formId,
		navigate,
		isFormRoute: !!formId,
	};
};

/**
 * Custom hook for submission-specific route handling
 */
export const useSubmissionRoute = () => {
	const { submissionId, currentView, navigate } = useFormsRouteParams();
	return {
		submissionId,
		isViewMode: currentView === 'view-submission',
		isEditMode: currentView === 'edit-submission',
		navigate,
	};
};